// Client-side PostHog (posthog-js). This is the browser SDK — pageviews,
// autocapture, web analytics, and session replay. It is separate from the
// server-side posthog-node capture in the Worker (src/worker/posthog.ts);
// both resolve to the same person because we identify() by email below.
//
// posthog-js is ~70kB gzipped, so it is loaded via dynamic import() into its
// own async chunk — the same reason Phaser is kept off the login/onboarding
// path. It never blocks first paint.
//
// The project token is a *public* ingestion token (the same one lives in
// wrangler.jsonc and ships in every PostHog client snippet), so embedding it
// here is expected — it is not a secret. An env override is supported for
// flexibility but not required.
import type { PostHog } from "posthog-js";

const KEY = import.meta.env.VITE_POSTHOG_KEY ?? "phc_BdzEn53K4HzTuhSSfKtccDwvdYrzHG9mZhveyV7aFL6K";
const HOST = import.meta.env.VITE_POSTHOG_HOST ?? "https://eu.i.posthog.com";

// Don't pollute the project with events/replays from local dev.
const isLocal =
  window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

let ph: PostHog | null = null;
// Non-null once initAnalytics() has kicked off the dynamic import; identify/reset
// chain off it so they still work if called before the chunk finishes loading.
let loading: Promise<void> | null = null;

export function initAnalytics(): void {
  if (isLocal || !KEY || loading) return;
  loading = import("posthog-js").then(({ default: posthog }) => {
    posthog.init(KEY, {
      api_host: HOST,
      defaults: "2026-05-30",
      // Explicit so web-analytics bounce rate / session duration work regardless
      // of the defaults snapshot. $pageleave only fires when a user leaves the
      // page, so it's normal for PostHog to warn until real sessions end.
      capture_pageview: true,
      capture_pageleave: true,
      // Only build person profiles for signed-in parents, not every anonymous visitor.
      person_profiles: "identified_only",
      // Session replay ON. We mask form inputs (the login email is PII) but leave
      // on-screen text visible so replays are readable. captureCanvas records the
      // Phaser/WebGL game <canvas> — without it the game area is blank in replays.
      // Canvas capture is low-FPS by design (a few frames/sec) and needs Phaser's
      // preserveDrawingBuffer (set in src/game/main.ts) or the readback is empty.
      disable_session_recording: false,
      session_recording: {
        maskAllInputs: true,
        captureCanvas: {
          recordCanvas: true,
          canvasFps: 3,
          canvasQuality: "0.6",
        },
      },
    });
    ph = posthog;
  });
}

/** Link browser events to the signed-in parent (matches the server's distinctId: email). */
export function identifyUser(email: string): void {
  if (isLocal || !loading) return;
  void loading.then(() => ph?.identify(email, { email }));
}

/** Clear identity on sign-out so the next user isn't merged into this profile. */
export function resetAnalytics(): void {
  if (isLocal || !loading) return;
  void loading.then(() => ph?.reset());
}
